// radar-map.jsx — real Leaflet + RainViewer radar.
// Mirrors wx-app.js radar logic but standalone Preact. Lazy-loads Leaflet
// + the frame manifest only after the section enters the viewport.

import { useEffect, useRef, useState } from 'preact/hooks';
import { loadLeaflet, whenInView } from './lazy-leaflet.js';
import { getRawState, subscribe } from '../data-bridge.js';

const RAINVIEWER_URL = '/api/ext/rainviewer/frames';
const RADAR_DEFAULT_ZOOM = 7;
const RADAR_TILE_SIZE = 256;
const RADAR_MAX_PAST = 6;
const RADAR_MAX_NOWCAST = 3;
const RADAR_FRAME_INTERVAL_MS = 450;

function proxyTileUrl(rawTpl) {
  return '/api/ext/img?url=' + encodeURIComponent(rawTpl)
    .replace(/%7B/g, '{').replace(/%7D/g, '}');
}
function cartoBasemapTpl(mode) {
  const style = mode === 'light' ? 'light_all' : 'dark_all';
  return `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png`;
}

async function fetchRadarFrames() {
  try {
    const r = await fetch(RAINVIEWER_URL);
    if (!r.ok) return null;
    return await r.json();
  } catch (_) { return null; }
}

export function RadarMap({ brand, height = 320, interactive: alwaysInteractive = false }) {
  const wrapRef = useRef(null);
  const mapDivRef = useRef(null);
  const stateRef = useRef({ map: null, base: null, layers: [], frames: [], frameIdx: 0, host: 'https://tilecache.rainviewer.com' });
  const [opacity, setOpacity] = useState(0.7);
  const [playing, setPlaying] = useState(false);
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [interactive, setInteractive] = useState(alwaysInteractive);

  // Mount Leaflet + frames when the section scrolls into view.
  useEffect(() => {
    if (!wrapRef.current) return;
    const stop = whenInView(wrapRef.current, async () => {
      try {
        const L = await loadLeaflet();
        const loc = getRawState().location;
        if (!loc || !mapDivRef.current) return;
        const map = L.map(mapDivRef.current, {
          center: [loc.lat, loc.lon],
          zoom: RADAR_DEFAULT_ZOOM,
          zoomControl: interactive,
          attributionControl: true,
          scrollWheelZoom: false,
          // Disable touch drag/zoom until the user taps the overlay. Without
          // this, single-finger touches inside the map trap page-scroll.
          dragging: interactive,
          touchZoom: interactive,
          doubleClickZoom: interactive,
          boxZoom: false,
          keyboard: false,
        });
        const base = L.tileLayer(proxyTileUrl(cartoBasemapTpl('dark')), {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
          subdomains: 'abcd', maxZoom: 19,
        }).addTo(map);
        if (!map.getPane('radarPane')) {
          map.createPane('radarPane');
          const pane = map.getPane('radarPane');
          if (pane) pane.style.zIndex = '450';
        }
        stateRef.current.map = map;
        stateRef.current.base = base;
        setReady(true);

        // "You are here" pin
        L.circleMarker([loc.lat, loc.lon], { radius: 6, color: brand.accent, fillColor: brand.accent, fillOpacity: 1, weight: 2 }).addTo(map);

        const data = await fetchRadarFrames();
        if (!data) { setError('RainViewer unavailable'); return; }
        stateRef.current.host = String(data.host || stateRef.current.host).replace(/\/+$/, '');
        const past = (data.radar?.past || []).slice(-RADAR_MAX_PAST);
        const nowcast = (data.radar?.nowcast || []).slice(0, RADAR_MAX_NOWCAST);
        stateRef.current.frames = past.concat(nowcast);
        stateRef.current.frameIdx = Math.max(0, past.length - 1);
        primeLayers(L);
        syncOpacity();

        // After tiles load, ensure layout
        setTimeout(() => map.invalidateSize(), 100);
      } catch (e) {
        console.warn('[m] radar init failed:', e);
        setError('Map failed to load');
      }
    });
    return () => {
      stop();
      const st = stateRef.current;
      if (playerRef.current) clearInterval(playerRef.current);
      if (st.map) { try { st.map.remove(); } catch (_) {} }
    };
  }, []);

  // React to location changes — recenter + re-prime
  useEffect(() => {
    return subscribe((s) => {
      const st = stateRef.current;
      if (!st.map || !s.location) return;
      st.map.setView([s.location.lat, s.location.lon], RADAR_DEFAULT_ZOOM);
    });
  }, []);

  function primeLayers(L) {
    const st = stateRef.current;
    if (!st.map) return;
    st.layers.forEach((layer) => { try { st.map.removeLayer(layer); } catch (_) {} });
    st.layers = st.frames.map((frame, idx) => {
      const path = frame.path || '';
      const base = path.indexOf('http') === 0 ? path : (st.host + path);
      const url = proxyTileUrl(base + '/' + RADAR_TILE_SIZE + '/{z}/{x}/{y}/2/1_1.png');
      const layer = L.tileLayer(url, {
        opacity: idx === st.frameIdx ? opacity : 0,
        pane: 'radarPane', tileSize: RADAR_TILE_SIZE, maxZoom: 19,
        keepBuffer: 2, updateWhenIdle: false, updateWhenZooming: false,
      });
      layer.addTo(st.map);
      return layer;
    });
  }
  function syncOpacity(val = opacity) {
    const st = stateRef.current;
    st.layers.forEach((layer, idx) => { layer.setOpacity(idx === st.frameIdx ? val : 0); });
  }

  const togglePlay = () => {
    if (playing) {
      clearInterval(playerRef.current); playerRef.current = null;
      setPlaying(false);
    } else {
      const st = stateRef.current;
      if (st.frames.length < 2) return;
      playerRef.current = setInterval(() => {
        st.frameIdx = (st.frameIdx + 1) % st.frames.length;
        syncOpacity();
      }, RADAR_FRAME_INTERVAL_MS);
      setPlaying(true);
    }
  };

  const enableInteractive = () => {
    if (interactive) return;
    setInteractive(true);
    const st = stateRef.current;
    if (st.map) {
      try {
        st.map.dragging.enable();
        st.map.touchZoom.enable();
        st.map.doubleClickZoom.enable();
        if (st.map.zoomControl) st.map.zoomControl.addTo(st.map);
      } catch (_) {}
    }
  };

  return (
    <div ref={wrapRef} className={`m-map-wrap${interactive ? ' is-interactive' : ''}`} style={{ position: 'relative' }}>
      <div ref={mapDivRef} style={{
        height, width: '100%', borderRadius: 16, overflow: 'hidden',
        border: `1px solid ${brand.line}`, background: '#0a1220',
      }}/>
      {!interactive && ready && (
        <div className="m-map-overlay" onClick={enableInteractive}>
          <span className="m-map-overlay-pill">Tap to interact</span>
        </div>
      )}
      {ready && (
        <div style={{ position: 'absolute', bottom: 8, left: 8, right: 8, display: 'flex', alignItems: 'center', gap: 8, padding: 8, background: 'rgba(0,0,0,.55)', borderRadius: 12, backdropFilter: 'blur(8px)' }}>
          <button onClick={togglePlay} aria-label={playing ? 'Pause radar' : 'Play radar'} style={{
            width: 32, height: 32, borderRadius: 16, background: brand.accent, color: brand.bg,
            border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
          }}>{playing ? '⏸' : '▶'}</button>
          <input
            type="range" min="0" max="100" value={opacity * 100}
            onInput={(e) => { const v = Number(e.target.value) / 100; setOpacity(v); syncOpacity(v); }}
            style={{ flex: 1 }}
            aria-label="Radar opacity"
          />
          <span style={{ fontSize: 10, color: '#fff', fontFamily: brand.eyebrowFont, minWidth: 30, textAlign: 'right' }}>{Math.round(opacity * 100)}%</span>
        </div>
      )}
      <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 4 }}>
        {[['Light','#60a5fa'],['Mod','#facc15'],['Heavy','#f87171']].map(([l,c]) => (
          <span key={l} style={{ fontSize: 9, color: c, background: 'rgba(0,0,0,.55)', padding: '3px 7px', borderRadius: 999, fontFamily: brand.eyebrowFont }}>{l}</span>
        ))}
      </div>
      {error && (
        <div style={{ position: 'absolute', top: 50, left: 0, right: 0, textAlign: 'center', color: brand.textDim, fontSize: 12 }}>{error}</div>
      )}
    </div>
  );
}
